// frontend/src/components/Booking/BookingCard.tsx
// UPDATED: Fixed to show actual service names and proper provider details

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  MapPin, 
  User, 
  Building2, 
  Package, 
  Phone, 
  Mail,
  // DollarSign, // Unused but available for pricing displays
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Check,
  X
} from 'lucide-react';
import { Booking, BookingStatus } from '@/types/booking';
import { ActionsDropdown, createCommonActions } from '@/components/common/ui';

interface BookingCardProps {
  booking: Booking;
  currentUserRole: 'admin' | 'provider' | 'customer';
  onAction?: (action: string, bookingId: number) => void;
  compact?: boolean;
  className?: string;
}

// Status configuration
const getStatusConfig = (status: BookingStatus) => {
  switch (status) {
    case 'pending':
      return { 
        icon: Clock, 
        color: 'text-yellow-600', 
        bg: 'bg-yellow-100',
        label: 'Pending'
      };
    case 'accepted':
      return { 
        icon: CheckCircle, 
        color: 'text-blue-600', 
        bg: 'bg-blue-100',
        label: 'Accepted'
      };
    case 'completed':
      return { 
        icon: CheckCircle, 
        color: 'text-green-600', 
        bg: 'bg-green-100',
        label: 'Completed'
      };
    case 'rejected':
      return { 
        icon: XCircle, 
        color: 'text-red-600', 
        bg: 'bg-red-100',
        label: 'Rejected'
      };
    case 'cancelled':
      return { 
        icon: XCircle, 
        color: 'text-red-600', 
        bg: 'bg-red-100',
        label: 'Cancelled'
      };
    default:
      return { 
        icon: AlertCircle, 
        color: 'text-gray-600', 
        bg: 'bg-gray-100',
        label: status
      };
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatTime = (dateString: string) => {
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

const BookingCard: React.FC<BookingCardProps> = ({
  booking,
  currentUserRole,
  onAction,
  compact = false,
  className = ''
}) => {
  const navigate = useNavigate();
  const statusConfig = getStatusConfig(booking.status);
  const StatusIcon = statusConfig.icon;

  const handleCardClick = () => {
    const basePath = currentUserRole === 'customer' 
      ? '/account/bookings' 
      : '/bookings';
    navigate(`${basePath}/${booking.booking_id}`);
  };

  return (
    <div 
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer ${className}`}
      onClick={handleCardClick}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <span className={`text-sm font-medium ${compact ? 'text-xs' : 'text-sm'}`}>
            #{booking.booking_id}
          </span>
          
          {/* Status Badge */}
          <span className={`inline-flex items-center px-2 py-1 rounded-full font-medium ${statusConfig.bg} ${statusConfig.color} ${compact ? 'text-xs' : 'text-xs'}`}>
            <StatusIcon className={`mr-1 ${compact ? 'h-3 w-3' : 'h-3 w-3'}`} />
            {statusConfig.label}
          </span>
        </div>
        
        <span className={`text-gray-500 ${compact ? 'text-xs' : 'text-xs'}`}>
          {formatDate(booking.created_at)}
        </span>
      </div>

      {/* Service Name - Enhanced Display */}
      <div className="mb-3">
        <div className="flex items-center space-x-2">
          <Package className={`text-gray-400 flex-shrink-0 ${compact ? 'h-4 w-4' : 'h-4 w-4'}`} />
          <div className="min-w-0 flex-1">
            <h3 className={`font-medium text-gray-900 truncate ${compact ? 'text-sm' : 'text-base'}`}>
              {booking.service_name || 'Service Request'}
            </h3>
            {booking.service_price && (
              <p className={`text-gray-500 ${compact ? 'text-xs' : 'text-sm'}`}>
                {booking.service_price}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Customer Information - Show for admin and provider only */}
      {currentUserRole !== 'customer' && !compact && (
        <div className="mb-3">
          <div className="flex items-center space-x-2">
            <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {booking.customer_name || 'Customer'}
              </p>
              {booking.customer_email && (
                <p className="text-xs text-gray-600 truncate">
                  {booking.customer_email}
                </p>
              )}
              {booking.customer_phone && (
                <p className="text-xs text-gray-600">
                  {booking.customer_phone}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Provider Information - Show for admin and customer only */}
      {currentUserRole !== 'provider' && !compact && (
        <div className="mb-3">
          <div className="flex items-center space-x-2">
            <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {booking.business_name || booking.provider_name || 'Service Provider'}
              </p>
              {booking.contact_person && (
                <p className="text-xs text-gray-600 truncate">
                  Contact: {booking.contact_person}
                </p>
              )}
              <div className="flex flex-col space-y-1">
                {(booking.contact_phone || booking.provider_phone) && (
                  <p className="text-xs text-gray-600 flex items-center">
                    <Phone className="h-3 w-3 mr-1" />
                    {booking.contact_phone || booking.provider_phone}
                  </p>
                )}
                {(booking.contact_email || booking.provider_email) && (
                  <p className="text-xs text-gray-600 flex items-center">
                    <Mail className="h-3 w-3 mr-1" />
                    {booking.contact_email || booking.provider_email}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Service Details */}
      <div className={`space-y-2 ${compact ? 'text-xs' : 'text-sm'}`}>
        {/* Date */}
        <div className="flex items-center text-gray-600">
          <Calendar className="h-3 w-3 mr-2" />
          <span>{formatDate(booking.service_date)}</span>
          {booking.preferred_time && (
            <span className="ml-2 text-gray-500">at {booking.preferred_time}</span>
          )}
        </div>

        {/* Location */}
        {booking.location && (
          <div className="flex items-center text-gray-600">
            <MapPin className="h-3 w-3 mr-2" />
            <span className="truncate">{booking.location}</span>
          </div>
        )}

        {/* Description Preview */}
        {!compact && booking.description && (
          <div className="text-gray-600 line-clamp-2 mt-2">
            {booking.description}
          </div>
        )}
      </div>

      {/* Footer */}
      {!compact && (
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <div className="text-xs text-gray-500">
            Created: {formatDate(booking.created_at)} at {formatTime(booking.created_at)}
          </div>
          
          {/* Actions Dropdown */}
          {onAction && currentUserRole === 'provider' && (
            <div onClick={(e) => e.stopPropagation()}>
              <ActionsDropdown
                size="sm"
                actions={[
                  ...(booking.status === 'pending' ? [
                    {
                      id: 'accept',
                      label: 'Accept Booking',
                      icon: Check,
                      onClick: () => onAction('accept', booking.booking_id),
                      variant: 'success' as const
                    },
                    {
                      id: 'reject',
                      label: 'Reject Booking',
                      icon: X,
                      onClick: () => onAction('reject', booking.booking_id),
                      variant: 'danger' as const
                    }
                  ] : []),
                  ...(booking.status === 'accepted' ? [
                    {
                      id: 'complete',
                      label: 'Mark Complete',
                      icon: CheckCircle,
                      onClick: () => onAction('complete', booking.booking_id),
                      variant: 'primary' as const
                    }
                  ] : [])
                ]}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingCard;